"use client";

import * as React from "react";
import type { Area, MediaSize } from "react-easy-crop";
import Cropper from "react-easy-crop";
import { getCroppedImageFile } from "@/lib/cropImage";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";

type RatioMode = "1-1" | "4-3" | "3-4" | "3-2" | "2-3" | "original";

function modeToAspect(mode: RatioMode, natural: number): number {
  if (mode === "original") {
    return natural > 0 ? natural : 1;
  }
  if (mode === "1-1") return 1;
  if (mode === "4-3") return 4 / 3;
  if (mode === "3-4") return 3 / 4;
  if (mode === "3-2") return 3 / 2;
  return 2 / 3;
}

type Props = {
  open: boolean;
  imageSrc: string;
  sourceType: string;
  sourceFileName: string;
  onOpenChange: (open: boolean) => void;
  onComplete: (file: File) => void;
};

export function BoozeImageCropDialog({
  open,
  imageSrc,
  sourceType,
  sourceFileName,
  onOpenChange,
  onComplete,
}: Props) {
  const [crop, setCrop] = React.useState({ x: 0, y: 0 });
  const [zoom, setZoom] = React.useState(1);
  const [rotation] = React.useState(0);
  const [ratioMode, setRatioMode] = React.useState<RatioMode>("1-1");
  const [naturalAspect, setNaturalAspect] = React.useState(1);
  const cropPixelsRef = React.useRef<Area | null>(null);
  const [applying, setApplying] = React.useState(false);

  const aspect = React.useMemo(
    () => modeToAspect(ratioMode, naturalAspect),
    [ratioMode, naturalAspect]
  );

  const onCropComplete = React.useCallback(
    (_area: Area, areaPixels: Area) => {
      cropPixelsRef.current = areaPixels;
    },
    []
  );

  const onMediaLoaded = React.useCallback((m: MediaSize) => {
    const w = m.naturalWidth || 1;
    const h = m.naturalHeight || 1;
    setNaturalAspect(w / h);
  }, []);

  React.useEffect(() => {
    if (open) {
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRatioMode("1-1");
      cropPixelsRef.current = null;
    }
  }, [open, imageSrc]);

  async function apply() {
    const area = cropPixelsRef.current;
    if (!area) {
      return;
    }
    setApplying(true);
    try {
      const file = await getCroppedImageFile(
        imageSrc,
        area,
        sourceType,
        sourceFileName
      );
      onComplete(file);
    } finally {
      setApplying(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[100dvh] max-w-2xl gap-0 overflow-y-auto p-0 sm:max-w-2xl"
        showCloseButton
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="p-4 pb-2 sm:p-6 sm:pb-3">
          <DialogHeader>
            <DialogTitle>Crop photo</DialogTitle>
            <DialogDescription>
              Drag the photo to position it, zoom as needed, then use apply. Only the
              area inside the frame is uploaded.
            </DialogDescription>
          </DialogHeader>
        </div>

        <div className="relative w-full">
          <div
            className="relative w-full"
            style={{ height: "min(56dvh, 400px)" }}
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={aspect}
              minZoom={1}
              maxZoom={3}
              cropShape="rect"
              showGrid
              objectFit="contain"
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              onMediaLoaded={onMediaLoaded}
              style={{}}
              classes={{}}
              mediaProps={{}}
              cropperProps={{}}
            />
          </div>
        </div>

        <div className="space-y-3 border-t px-4 py-3 sm:px-6 sm:py-4">
          <div className="space-y-2">
            <Label className="text-xs text-muted-foreground">Proportion</Label>
            <div className="flex flex-wrap gap-2">
              {(
                [
                  ["1-1" as const, "1:1"],
                  ["4-3" as const, "4:3"],
                  ["3-4" as const, "3:4"],
                  ["3-2" as const, "3:2"],
                  ["2-3" as const, "2:3"],
                  ["original" as const, "Original"],
                ] as const
              ).map(([id, label]) => (
                <Button
                  key={id}
                  type="button"
                  size="sm"
                  variant={ratioMode === id ? "secondary" : "outline"}
                  onClick={() => setRatioMode(id)}
                >
                  {label}
                </Button>
              ))}
            </div>
          </div>
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 text-xs text-muted-foreground">
              <span>Zoom</span>
              <span className="font-mono tabular-nums">{zoom.toFixed(2)}</span>
            </div>
            <input
              type="range"
              className={cn("h-2 w-full cursor-pointer appearance-none rounded-lg bg-secondary accent-primary")}
              min={1}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
            />
          </div>
        </div>

        <DialogFooter className="gap-2 border-t px-4 py-3 sm:px-6 sm:py-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={applying}
          >
            Cancel
          </Button>
          <Button type="button" onClick={apply} disabled={applying}>
            {applying ? "Cropping…" : "Use cropped photo"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
